import React from 'react';

const GeneralTab = ({ contract, onChange, validationErrors = {} }) => {
    const handleChange = (field, value) => {
        onChange({ [field]: value });
    };

    // Contract type options
    const typeOptions = [
        { value: 0, label: '- - -' },
        { value: 1, label: 'Jobs' },
        { value: 2, label: 'Hochzeiten' }
    ];

    // Styles
    const labelStyle = {
        display: 'block',
        marginBottom: '0.5rem',
        fontWeight: '500',
        color: 'var(--text-primary, #2c3e50)',
        fontSize: '0.875rem'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.625rem 1rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: 'var(--text-primary, #2c3e50)',
        background: 'var(--bg-card, #ffffff)',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    };

    const inputErrorStyle = {
        ...inputStyle,
        borderColor: '#ef4444'
    };

    const errorTextStyle = {
        color: '#ef4444',
        fontSize: '0.75rem',
        marginTop: '0.25rem'
    };

    const selectStyle = {
        ...inputStyle,
        cursor: 'pointer'
    };

    const switchContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderRadius: '8px',
        border: '1px solid var(--border-light, #e2e8f0)'
    };

    const switchLabelStyle = {
        color: 'var(--text-primary, #2c3e50)',
        fontSize: '0.875rem',
        cursor: 'pointer'
    };

    const helpTextStyle = {
        color: 'var(--text-secondary, #64748b)',
        fontSize: '0.75rem',
        marginTop: '0.5rem'
    };

    return (
        <div className="contract-general-tab">
            <div className="row">
                {/* Title */}
                <div className="col-md-8 mb-3">
                    <label style={labelStyle} htmlFor="contractTitle">
                        Titel <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        type="text"
                        id="contractTitle"
                        style={validationErrors.title ? inputErrorStyle : inputStyle}
                        value={contract?.title || ''}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="z.B. Standard Hochzeitsvertrag"
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--accent-slate, #5b7c8d)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(91, 124, 141, 0.08)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = validationErrors.title ? '#ef4444' : 'var(--border-medium, #cbd5e1)';
                            e.target.style.boxShadow = 'none';
                        }}
                    />
                    {validationErrors.title && (
                        <div style={errorTextStyle}>{validationErrors.title}</div>
                    )}
                </div>

                {/* Category/Type */}
                <div className="col-md-4 mb-3">
                    <label style={labelStyle} htmlFor="contractType">
                        Kategorie <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                        id="contractType"
                        style={validationErrors.type ? { ...selectStyle, borderColor: '#ef4444' } : selectStyle}
                        value={contract?.type || 0}
                        onChange={(e) => handleChange('type', parseInt(e.target.value))}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--accent-slate, #5b7c8d)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(91, 124, 141, 0.08)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = validationErrors.type ? '#ef4444' : 'var(--border-medium, #cbd5e1)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        {typeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {validationErrors.type && (
                        <div style={errorTextStyle}>{validationErrors.type}</div>
                    )}
                </div>
            </div>

            {/* Cancellation Policy */}
            <div className="row">
                <div className="col-12 mb-3">
                    <label style={{ ...labelStyle, marginBottom: '0.75rem' }}>
                        Widerrufserklärung
                    </label>
                    <div style={switchContainerStyle}>
                        <div className="form-check form-switch" style={{ margin: 0, padding: 0 }}>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="cancellationPolicy"
                                checked={contract?.cancellation_policy_obligation === 1}
                                onChange={(e) => handleChange('cancellation_policy_obligation', e.target.checked ? 1 : 0)}
                                style={{
                                    width: '2.5rem',
                                    height: '1.25rem',
                                    cursor: 'pointer',
                                    marginLeft: 0
                                }}
                            />
                        </div>
                        <label htmlFor="cancellationPolicy" style={switchLabelStyle}>
                            Muss akzeptiert werden
                        </label>
                    </div>
                    <p style={helpTextStyle}>
                        Wenn aktiviert, muss der Kunde die Widerrufserklärung beim Unterzeichnen des Vertrags akzeptieren.
                    </p>
                </div>
            </div>

            {/* Info Box */}
            <div className="row">
                <div className="col-12">
                    <div style={{
                        padding: '1rem',
                        background: 'rgba(91, 124, 141, 0.08)',
                        borderRadius: '8px',
                        border: '1px solid rgba(91, 124, 141, 0.15)'
                    }}>
                        <p style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            color: 'var(--text-secondary, #64748b)'
                        }}>
                            <strong style={{ color: 'var(--text-primary, #2c3e50)' }}>Hinweis:</strong>{' '}
                            Die Kategorie bestimmt, welche Variablen im Vertragsinhalt zur Verfügung stehen.
                            Jobs-Verträge erhalten Job-spezifische Variablen, Hochzeits-Verträge erhalten
                            Hochzeits-spezifische Variablen.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeneralTab;
